'use client'

import { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { SPECIALTY_INFO, type SpecialtyInfo } from '@/lib/format'

export interface SpecialtyConfig {
  specialty: string
  label: string
  shortLabel: string
  icon: string
  description: string
  category: 'EVENT' | 'TRAINING' | 'BOTH' | string
  active: boolean
  sortOrder: number
  subcategory?: string | null
  keywords?: string[]
  domainId: string | null
  domain: { id: string; slug: string; label: string; icon: string; active: boolean } | null
}

export interface DomainGroup {
  id: string | null
  slug: string | null
  label: string
  icon: string
  specialties: SpecialtyConfig[]
}

const DEFAULT_BADGE = 'bg-white/[0.08] border-white/[0.15] text-text-secondary'

// Couleur de badge : l'ancien référentiel statique sert de fallback visuel, la config admin fait foi pour le reste
export function specialtyBadge(cfg: Pick<SpecialtyConfig, 'specialty' | 'icon' | 'shortLabel' | 'label'> | undefined, key: string): SpecialtyInfo {
  const legacy = SPECIALTY_INFO[key]
  return {
    label: cfg?.label ?? legacy?.label ?? key,
    shortLabel: cfg?.shortLabel ?? legacy?.shortLabel ?? key,
    icon: cfg?.icon ?? legacy?.icon ?? '🔧',
    description: legacy?.description ?? '',
    badgeClass: legacy?.badgeClass ?? DEFAULT_BADGE,
  }
}

// Une seule requête par chargement de page, même si plusieurs composants utilisent le hook
let configPromise: Promise<SpecialtyConfig[]> | null = null
function loadConfigs(): Promise<SpecialtyConfig[]> {
  if (!configPromise) {
    configPromise = apiFetch<SpecialtyConfig[]>('/public/specialty-config').catch(() => {
      configPromise = null
      return [] as SpecialtyConfig[]
    })
  }
  return configPromise
}

export function useSpecialtyConfig(opts: { activeOnly?: boolean } = { activeOnly: true }) {
  const [configs, setConfigs] = useState<SpecialtyConfig[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    loadConfigs()
      .then((all) => {
        if (cancelled) return
        setConfigs(opts.activeOnly === false ? all : all.filter((c) => c.active && c.domain?.active !== false))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.activeOnly])

  const byKey = useMemo(() => new Map(configs.map((c) => [c.specialty, c])), [configs])

  const byDomain = useMemo<DomainGroup[]>(() => {
    const groups = new Map<string, DomainGroup>()
    for (const c of configs) {
      const id = c.domain?.id ?? '__none__'
      if (!groups.has(id)) {
        groups.set(id, {
          id: c.domain?.id ?? null,
          slug: c.domain?.slug ?? null,
          label: c.domain?.label ?? 'Autres',
          icon: c.domain?.icon ?? '🔧',
          specialties: [],
        })
      }
      groups.get(id)!.specialties.push(c)
    }
    // Sous-catégories contiguës pour l'affichage groupé, ordre admin à l'intérieur
    for (const g of groups.values()) {
      g.specialties.sort((a, b) => (a.subcategory ?? '').localeCompare(b.subcategory ?? '', 'fr') || a.sortOrder - b.sortOrder)
    }
    return [...groups.values()]
  }, [configs])

  const badge = (key: string) => specialtyBadge(byKey.get(key), key)

  return { configs, byKey, byDomain, badge, loading }
}
